import React, { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import axios from "axios";
import { API, useLanguage, useAuth } from "../App";
import { Button } from "../components/ui/button";
import { 
  AlertTriangle, 
  CreditCard, 
  Download, 
  LogOut,
  Check,
  Sparkles,
  ArrowRight,
  Zap
} from "lucide-react";

const SubscriptionRequiredPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { language } = useLanguage();
  const { user, logout } = useAuth();
  
  const [loading, setLoading] = useState(true);
  const [subscriptionStatus, setSubscriptionStatus] = useState(null);
  const [selectedTier, setSelectedTier] = useState("growth");
  const [selectedPlan, setSelectedPlan] = useState("monthly");
  const [processingPayment, setProcessingPayment] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [deactivating, setDeactivating] = useState(false);

  useEffect(() => {
    fetchSubscriptionStatus();
    if (searchParams.get("subscription_success") === "true") {
      const sessionId = searchParams.get("session_id");
      if (sessionId) activateSubscription(sessionId);
    }
  }, []);

  const fetchSubscriptionStatus = async () => {
    try {
      const response = await axios.get(`${API}/subscription/status`, { withCredentials: true });
      setSubscriptionStatus(response.data);
      if (response.data.can_access) navigate("/dashboard");
    } catch (error) {
      console.error("Error fetching subscription status:", error);
    } finally {
      setLoading(false);
    }
  };

  const activateSubscription = async (sessionId) => {
    try {
      const response = await axios.post(`${API}/subscription/activate/${sessionId}`, {}, { withCredentials: true });
      if (response.data.success) {
        toast.success(language === "it" ? "Abbonamento attivato!" : "Subscription activated!");
        navigate("/dashboard");
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Errore nell'attivazione");
    }
  };

  const handleSubscribe = async () => {
    setProcessingPayment(true);
    try {
      const response = await axios.post(`${API}/subscription/checkout`, {
        plan: selectedPlan,
        tier: selectedTier,
        origin_url: window.location.origin
      }, { withCredentials: true });
      if (response.data.checkout_url) window.location.href = response.data.checkout_url;
    } catch (error) {
      toast.error(error.response?.data?.detail || "Errore");
      setProcessingPayment(false);
    }
  };

  const handleDownloadData = async () => {
    setDownloading(true);
    try {
      const response = await axios.get(`${API}/subscription/export/all-data`, { withCredentials: true, responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `SmartBooker_Export_${new Date().toISOString().split('T')[0]}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success(language === "it" ? "Dati scaricati!" : "Data downloaded!");
    } catch {
      toast.error("Errore nel download");
    } finally {
      setDownloading(false);
    }
  };

  const handleDeactivate = async () => {
    if (!window.confirm(language === "it" 
      ? "Sei sicuro di voler disattivare il tuo account?"
      : "Are you sure you want to deactivate your account?"
    )) return;
    setDeactivating(true);
    try {
      await axios.post(`${API}/subscription/deactivate`, {}, { withCredentials: true });
      toast.success(language === "it" ? "Account disattivato" : "Account deactivated");
      logout();
      navigate("/");
    } catch {
      toast.error("Errore");
    } finally {
      setDeactivating(false);
    }
  };

  const tiers = [
    {
      key: "essential",
      name: "Essential",
      desc: language === "it" ? "Per chi inizia" : "Getting started",
      monthly: 34.95,
      annual: 349,
      features: language === "it"
        ? ["Prenotazioni 24/7", "CRM clienti base", "Promemoria standard", "SmartPage white-label", "Dashboard base", "Pagamenti online"]
        : ["24/7 bookings", "Basic CRM", "Standard reminders", "White-label SmartPage", "Basic dashboard", "Online payments"],
    },
    {
      key: "growth",
      name: "Growth",
      desc: language === "it" ? "Per crescere" : "For growth",
      monthly: 89,
      annual: 890,
      recommended: true,
      features: language === "it"
        ? ["Tutto di Essential +", "WhatsApp automation", "Follow-up automatici", "Analytics LTV", "Suggerimenti AI"]
        : ["Everything in Essential +", "WhatsApp automation", "Auto follow-ups", "LTV analytics", "AI suggestions"],
    },
    {
      key: "pro",
      name: "Pro",
      desc: language === "it" ? "Per dominare" : "For domination",
      monthly: 179,
      annual: 1790,
      features: language === "it"
        ? ["Tutto di Growth +", "WhatsApp campagne", "CRM scoring avanzato", "Revenue dashboard avanzata", "Multi-sede (10)", "Supporto prioritario"]
        : ["Everything in Growth +", "WhatsApp campaigns", "Advanced CRM scoring", "Advanced revenue dashboard", "Multi-location (10)", "Priority support"],
    },
  ];

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white dark:from-slate-950 dark:to-slate-900 flex items-center justify-center">
        <div className="animate-spin w-8 h-8 border-4 border-violet-500 border-t-transparent rounded-full" />
      </div>
    );
  }

  const currentTier = tiers.find(t => t.key === selectedTier) || tiers[1];

  return (
    <div className="min-h-screen bg-gradient-to-b from-amber-50 to-white dark:from-slate-950 dark:to-slate-900 flex items-center justify-center p-4">
      <div className="max-w-4xl w-full">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="w-20 h-20 rounded-full bg-amber-100 dark:bg-amber-900/30 flex items-center justify-center mx-auto mb-6">
            <AlertTriangle className="w-10 h-10 text-amber-500" />
          </div>
          <h1 className="text-3xl font-bold text-slate-900 dark:text-white mb-2" data-testid="subscription-title">
            {language === "it" ? "Scegli il tuo piano" : "Choose your plan"}
          </h1>
          <p className="text-slate-500 dark:text-slate-400 text-lg">
            {subscriptionStatus?.is_expired 
              ? (language === "it" ? "Il tuo periodo di prova è terminato." : "Your trial period has ended.")
              : (language === "it" ? "Seleziona il piano più adatto al tuo business." : "Select the plan that fits your business.")
            }
          </p>
          {subscriptionStatus?.salon_name && (
            <p className="text-slate-600 dark:text-slate-300 font-medium mt-2">{subscriptionStatus.salon_name}</p>
          )}
        </div>

        {/* Tier Selection Cards */}
        <div className="grid md:grid-cols-3 gap-4 mb-6">
          {tiers.map((tier) => (
            <label
              key={tier.key}
              className={`relative p-5 rounded-2xl border-2 cursor-pointer transition-all ${
                selectedTier === tier.key
                  ? "border-emerald-500 bg-emerald-50 dark:bg-emerald-900/20"
                  : "border-slate-200 dark:border-slate-600 hover:border-emerald-300"
              }`}
              data-testid={`tier-${tier.key}`}
            >
              <input type="radio" name="tier" value={tier.key} checked={selectedTier === tier.key} onChange={() => setSelectedTier(tier.key)} className="sr-only" />
              {tier.recommended && (
                <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-0.5 bg-emerald-500 text-white text-xs font-bold rounded-full flex items-center gap-1">
                  <Sparkles className="w-3 h-3" />{language === "it" ? "Il più Acquistato" : "Most purchased"}
                </div>
              )}
              <div className={tier.recommended ? "mt-1" : ""}>
                <h3 className="text-lg font-bold text-slate-900 dark:text-white">{tier.name}</h3>
                <p className="text-xs text-slate-500 mb-3">{tier.desc}</p>
                <p className="text-2xl font-bold text-emerald-600 dark:text-emerald-400">
                  €{selectedPlan === "annual" ? (tier.annual / 12).toFixed(0) : tier.monthly.toFixed(2).replace('.', ',')}
                  <span className="text-sm font-normal text-slate-500">/{language === "it" ? "mese" : "month"}</span>
                </p>
                {selectedPlan === "annual" && (
                  <p className="text-xs text-slate-500 mt-0.5">€{tier.annual}/{language === "it" ? "anno" : "year"}</p>
                )}
              </div>
              <div className="mt-3 space-y-1.5">
                {tier.features.map((f, i) => (
                  <div key={i} className="flex items-center gap-1.5">
                    <Check className="w-3.5 h-3.5 text-emerald-500 flex-shrink-0" />
                    <span className="text-xs text-slate-600 dark:text-slate-300">{f}</span>
                  </div>
                ))}
              </div>
              {selectedTier === tier.key && (
                <div className="absolute top-3 right-3 w-5 h-5 rounded-full bg-emerald-500 flex items-center justify-center">
                  <Check className="w-3 h-3 text-white" />
                </div>
              )}
            </label>
          ))}
        </div>

        {/* Billing Toggle */}
        <div className="bg-white dark:bg-slate-800 rounded-2xl p-6 shadow-xl mb-6">
          <div className="flex items-center justify-center gap-3 mb-6">
            <button
              onClick={() => setSelectedPlan("monthly")}
              className={`px-5 py-2 rounded-full text-sm font-medium transition-all ${selectedPlan === "monthly" ? "bg-emerald-500 text-white" : "bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-300"}`}
              data-testid="plan-monthly"
            >
              {language === "it" ? "Mensile" : "Monthly"}
            </button>
            <button
              onClick={() => setSelectedPlan("annual")}
              className={`px-5 py-2 rounded-full text-sm font-medium transition-all ${selectedPlan === "annual" ? "bg-emerald-500 text-white" : "bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-300"}`}
              data-testid="plan-annual"
            >
              {language === "it" ? "Annuale (-2 mesi)" : "Annual (-2 months)"}
            </button>
          </div>

          <div className="text-center mb-6">
            <p className="text-3xl font-bold text-slate-900 dark:text-white">
              €{selectedPlan === "annual" ? currentTier.annual : currentTier.monthly.toFixed(2).replace('.', ',')}
              <span className="text-base font-normal text-slate-500 ml-1">/{selectedPlan === "annual" ? (language === "it" ? "anno" : "year") : (language === "it" ? "mese" : "month")}</span>
            </p>
            <p className="text-sm text-slate-500 mt-1">
              Piano <span className="font-semibold text-emerald-600">{currentTier.name}</span>
              {selectedPlan === "annual" && ` — ${language === "it" ? "2 mesi gratis" : "2 months free"}`}
            </p>
          </div>

          <Button
            onClick={handleSubscribe}
            disabled={processingPayment}
            className="w-full py-6 text-lg font-semibold rounded-full bg-emerald-500 hover:bg-emerald-400 text-slate-950"
            data-testid="subscribe-btn"
          >
            {processingPayment ? (
              <span className="flex items-center gap-2">
                <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                {language === "it" ? "Reindirizzamento..." : "Redirecting..."}
              </span>
            ) : (
              <span className="flex items-center gap-2">
                <CreditCard className="w-5 h-5" />
                {language === "it" ? `Attiva ${currentTier.name}` : `Activate ${currentTier.name}`}
                <ArrowRight className="w-5 h-5" />
              </span>
            )}
          </Button>
        </div>

        {/* Alternative Options */}
        <div className="bg-white dark:bg-slate-800 rounded-2xl p-6 shadow-lg">
          <p className="text-sm text-slate-500 dark:text-slate-400 mb-4 text-center">
            {language === "it" 
              ? "Non vuoi continuare? Scarica i tuoi dati prima di andare."
              : "Don't want to continue? Download your data before you go."}
          </p>
          <div className="flex flex-col sm:flex-row gap-3">
            <Button variant="outline" onClick={handleDownloadData} disabled={downloading} className="flex-1 rounded-full" data-testid="download-data-btn">
              {downloading ? (
                <span className="flex items-center gap-2">
                  <div className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin" />
                  {language === "it" ? "Download..." : "Downloading..."}
                </span>
              ) : (
                <span className="flex items-center gap-2">
                  <Download className="w-4 h-4" />
                  {language === "it" ? "Scarica i miei dati" : "Download my data"}
                </span>
              )}
            </Button>
            <Button variant="ghost" onClick={handleDeactivate} disabled={deactivating} className="flex-1 rounded-full text-red-500 hover:text-red-600 hover:bg-red-50" data-testid="deactivate-btn">
              {deactivating ? "..." : (
                <span className="flex items-center gap-2">
                  <LogOut className="w-4 h-4" />
                  {language === "it" ? "Disattiva account" : "Deactivate account"}
                </span>
              )}
            </Button>
          </div>
        </div>

        <p className="text-center text-xs text-slate-400 mt-6">
          {language === "it" ? "Hai domande? Contattaci a info@smartbooker.it" : "Questions? Contact us at info@smartbooker.it"}
        </p>
      </div>
    </div>
  );
};

export default SubscriptionRequiredPage;
